'use client'

import { useCallback } from 'react'
import Link from 'next/link'
import { useHoverIntent } from 'react-use-hoverintent'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTag, faLaptop, faPeopleLine } from '@awesome.me/kit-94e593bf23/icons/sharp/light'

import { useAppContext } from '@/app/_lib/_context'

const TradeProgramPreview = ({ isLoggedIn, isTradeAccount, onClose, onOpenSignIn }) => {
    return (
        <div className="w-[320px] text-left">
            {/* Header */}
            <div className="px-4 pt-4 pb-2 border-b border-gray-5">
                <h3 className="text-sm font-medium text-ink-dark">A&D Trade Program</h3>
                <p className="text-3xs text-light mt-1 mb-0">
                    For over 20 years hive has been a trusted resource for A+D trade professionals.
                </p>
            </div>

            {/* Benefits */}
            <div className="px-4 pt-2 pb-3">
                <span className="text-3xs text-light font-medium uppercase tracking-wide">Program Benefits</span>
                <div className="mt-1.5 space-y-1.5">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faTag} className="text-sm text-primary w-4" />
                        <span className="text-sm">Exclusive Trade Pricing</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faLaptop} className="text-sm text-primary w-4" />
                        <span className="text-sm">Online Ordering</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faPeopleLine} className="text-sm text-primary w-4" />
                        <span className="text-sm">Supportive Community</span>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="px-4 py-3 bg-gray-1 rounded-b-lg">
                {isTradeAccount ? (
                    <Link
                        href="/hive-trade-program"
                        className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2 px-4 rounded transition-colors"
                        onClick={onClose}
                    >
                        View Trade Program
                    </Link>
                ) : isLoggedIn ? (
                    <Link
                        href="/hive-trade-program"
                        className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2 px-4 rounded transition-colors"
                        onClick={onClose}
                    >
                        Apply for Trade Program
                    </Link>
                ) : (
                    <Link
                        href="/hive-trade-program"
                        className="block w-full text-center bg-gray-5 hover:bg-primary text-ink hover:text-white text-sm font-medium py-2 px-4 rounded transition-colors"
                        onClick={onClose}
                    >
                        Apply
                    </Link>
                )}
            </div>
        </div>
    )
}

const HeaderTradeProgramLink = ({ className }) => {
    const pathname = usePathname()
    const { openSignInModal } = useAppContext()
    const { data: session } = useSession()

    // Hover intent for preview - desktop only
    const [isIntentHover, intentRef, setIsIntentHover] = useHoverIntent({
        sensitivity: 6,
        interval: 100,
        timeout: 300
    })

    const handleClosePreview = useCallback(() => {
        setIsIntentHover(false)
    }, [setIsIntentHover])

    const handleOpenSignIn = useCallback(() => {
        setIsIntentHover(false)
        openSignInModal()
    }, [setIsIntentHover, openSignInModal])

    // Don't show preview on trade program page
    const showPreview = isIntentHover && pathname !== '/hive-trade-program'

    return (
        <div ref={intentRef} className={`relative inline-flex items-center ${className || ''}`}>
            <Link
                href="/hive-trade-program"
                className="text-sm hover:text-primary transition-colors duration-200"
                onClick={handleClosePreview}
            >
                Trade Program
            </Link>

            {/* Trade Program preview popover - desktop only */}
            {showPreview && (
                <div
                    className="hidden lg:block absolute top-full right-0 pt-4 z-[100]"
                    role="dialog"
                    aria-label="Trade Program preview"
                >
                    {/* Arrow pointing up */}
                    <div className="absolute top-2 right-1 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white z-10" />
                    <div className="bg-white rounded-lg shadow-lg border border-gray-5 -mr-3">
                        <TradeProgramPreview
                            isLoggedIn={!!session}
                            isTradeAccount={!!session?.user?.tradeAccount}
                            onClose={handleClosePreview}
                            onOpenSignIn={handleOpenSignIn}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default HeaderTradeProgramLink
